import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en, type MessageKey } from './messages.en.ts';
import { th } from './messages.th.ts';
import {
  DEFAULT_LOCALE,
  applyDocumentLang,
  isLocaleStorageEvent,
  loadLocale,
  saveLocale,
  type Locale,
} from './locale.ts';
import { translate, type TFunction } from './translate.ts';

const DICTS: Record<Locale, Record<MessageKey, string>> = { en, th };

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * MVP-272 Phase 0. One provider, mounted once at the root (`main.tsx`), wrapping the whole app —
 * Stage, guest, and console all read the same `locale`/`t` from here as each later phase wires
 * them up; nothing downstream needs, or should mount, a second one.
 *
 * The stored/default locale is applied to `<html lang>` in an effect rather than read once at
 * mount, because `setLocale` below (the Settings language control's write path) must move the
 * attribute the moment the operator switches — `index.html`'s own `lang="th"` only covers the gap
 * before this effect runs at all, on the very first paint.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(loadLocale);

  useEffect(() => {
    applyDocumentLang(document.documentElement, locale);
  }, [locale]);

  // MVP-287. The Stage runs in its own window with its own copy of this provider, so a language
  // switch made in the cockpit never reaches it through React — only through the browser. A
  // document never receives its own `storage` event, so the writer here still updates from
  // `setLocale`'s state below and there is no echo to guard against; every OTHER open document
  // (the Stage, a guest tab) follows live instead of waiting for a reload nobody should have to
  // perform on an audience display.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (isLocaleStorageEvent(event.key)) setLocaleState(loadLocale());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  function setLocale(next: Locale) {
    setLocaleState(next);
    saveLocale(next);
  }

  const value = useMemo<I18nContextValue>(() => {
    const dict = DICTS[locale];
    const isDev = import.meta.env.DEV;
    return {
      locale,
      setLocale,
      t: (key, params) => translate(dict, key, params, isDev),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Throws rather than silently returning a default — the same "impossible to use it wrong without
 * finding out immediately" contract as `usePlaybackStateContext` (features/ops/usePlaybackState.ts):
 * a component rendered outside `I18nProvider` has no correct dictionary to fall back to. */
function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT()/useLocale() must be used within an I18nProvider');
  return ctx;
}

/** Returns `t(key, params?)` only — the narrow surface every translated component actually needs.
 * DEFAULT_LOCALE is re-exported here only so a caller that needs the constant (not the live value)
 * has one obvious place to import it from alongside the hook. */
export function useT(): TFunction {
  return useI18n().t;
}

export { DEFAULT_LOCALE };

/** Separate from `useT()` because almost every translated component needs only `t`; the Settings
 * language control (Phase 0's one caller) is the exception that needs to read and change the
 * active locale itself, not the common case. */
export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}
